import BaseBoosterBehavior, { IBaseBoosterBehaviorPlayMeta } from './BaseBoosterBehavior';
import { BoosterType, PreBoosterType } from '../BoosterType';
import { track, TrackMethod } from '../../../Utility/Tracking/Track';
import { TransactionType } from '../../../Utility/Tracking/DataSet/Transaction';
import { IGameState } from '../../Game/IGameState';
import CardStateHelper from '../CardStateHelper';
import { CanisterPreBoosterCardData, ICardsState } from '../ICardState';
import { CardTask } from '../CardTasks';
import { initialCardStateData } from '../initialCardStateData';
import { ILimit, MathHelper } from 'Utility/MathHelper';
import { Random } from 'Utility/Random';
import { CANISTER_PRE_BOOSTER_IN_STACK_PLACEMENT_RANGE } from 'Constants/PreBoosters';
import { MAX_OPENED_CARDS_IN_STACK } from 'Constants/InGame';
import { CardType } from 'Prototype/Level/Card/CardType';
import Point from 'Utility/Point';

export default class CanisterPreBoosterBehavior extends BaseBoosterBehavior {
    public play(state: IGameState, meta?: IBaseBoosterBehaviorPlayMeta): IGameState {
        let cardsState = { ...state.levelState.cardsState };
        const canisterCardID = `card_canister_${Random.ID()}`;

        // TODO: combine with common function Roman made for finding joker index
        const range: ILimit = CANISTER_PRE_BOOSTER_IN_STACK_PLACEMENT_RANGE;
        // +1 here because the card is not in the stack yet
        const stackCount = Math.min(MAX_OPENED_CARDS_IN_STACK, cardsState.inStack.length + 1);
        const randomDistanceFromTop = Random.int(
            // Min Range: If stack count is < min, then use stack count as the min else use default min
            stackCount < range.min ? stackCount : range.min,
            // Max range: Take which ever is lower, stack count or max range
            Math.min(stackCount, range.max)
        );
        // -1 here to adjust for count -> index
        const index = MathHelper.clamp(stackCount - randomDistanceFromTop, 0, stackCount - 1);

        cardsState = CanisterPreBoosterBehavior.addCanisterCard(cardsState, { id: canisterCardID });
        cardsState = CardStateHelper.setCardInStack(cardsState, canisterCardID, { index });

        // Queue reveal task
        cardsState = CardStateHelper.setStateTaskData(cardsState, {
            task: CardTask.REVEAL_CANISTER_PRE_BOOSTER,
            cards: [canisterCardID],
        });

        state = CardStateHelper.updateGameState(state, cardsState);

        return this.consumeItemOrCoins(state);
    }

    protected getPrice(state: IGameState): number {
        return state.levelState.boosterPrices[PreBoosterType.CANISTER];
    }

    public getInventoryItem(state: IGameState): number {
        return state.playerState.inventory[PreBoosterType.CANISTER];
    }

    protected spendInventoryItem(state: IGameState): IGameState {
        track('transactionType', TransactionType.INVENTORY);
        return this.decrementInventoryItem(state, PreBoosterType.CANISTER);
    }

    protected spendCoins(state: IGameState): IGameState {
        super.trackCoinsSpent(state, 'tCanisterCardBoostersCoins');
        state = super.spendCoins(state);
        return this.incrementBoosterUse(state, PreBoosterType.CANISTER);
    }

    protected consumeItemOrCoins(state: IGameState): IGameState {
        track('tCanisterCardBoostersUsed', 1, TrackMethod.ADD);
        track('mCanisterCardBoostersUsed', 1, TrackMethod.ADD);

        return super.consumeItemOrCoins(state);
    }

    public static addCanisterCard(state: ICardsState, data: Partial<CanisterPreBoosterCardData>): ICardsState {
        return CardStateHelper.addCard(state, {
            ...initialCardStateData,
            type: CardType.canister_pre_booster,
            faceUp: true,
            y: 23,
            offset: new Point(0, 0),
            ...data,
        });
    }
}
